import { useState } from 'react';
import { Download, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Badge,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@higgins/ui';
import { useCombinedReports, useDistributeCombined } from '@/lib/hooks';
import { fetchCombinedExport } from '@/lib/api';
import type { CombinedReportSummary, CombinedStatus } from '@/lib/types';

const STATUS_BADGE: Record<CombinedStatus, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  Draft:       { label: 'Draft',       variant: 'secondary' },
  Published:   { label: 'Published',   variant: 'outline'   },
  Distributed: { label: 'Distributed', variant: 'default'   },
};

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export function SavedDecks() {
  const { data: decks = [], isLoading } = useCombinedReports();
  const distribute = useDistributeCombined();

  const [confirmDeck, setConfirmDeck] = useState<CombinedReportSummary | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function handleDownload(deck: CombinedReportSummary) {
    setDownloadingId(deck.id);
    try {
      const { url } = await fetchCombinedExport(deck.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${deck.coverTitle || 'GM Combined Report'} - ${formatDate(deck.coverDate)}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error('Download failed', { description: e?.message ?? 'Please try again.' });
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleDistribute() {
    if (!confirmDeck) return;
    try {
      const result = await distribute.mutateAsync(confirmDeck.id);
      const skippedNote = result.skipped.length ? ` ${result.skipped.length} skipped.` : '';
      toast.success('Deck distributed', {
        description: `${result.distributed} report${result.distributed === 1 ? '' : 's'} marked Distributed.${skippedNote}`,
      });
    } catch (e: any) {
      toast.error('Distribute failed', { description: e?.message ?? 'Please try again.' });
    } finally {
      setConfirmDeck(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Saved decks</CardTitle>
        <CardDescription>Re-download a generated deck, or mark one distributed to lock its reports.</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading decks…
          </div>
        ) : decks.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No combined decks yet. Build one above to get started.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Cover date</TableHead>
                <TableHead className="text-center">Reports</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Generated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {decks.map(deck => {
                const { label, variant } = STATUS_BADGE[deck.status];
                const canDistribute = deck.status !== 'Distributed';
                return (
                  <TableRow key={deck.id}>
                    <TableCell className="font-medium">{deck.coverTitle || 'Untitled deck'}</TableCell>
                    <TableCell>{formatDate(deck.coverDate)}</TableCell>
                    <TableCell className="text-center">{deck.reportCount}</TableCell>
                    <TableCell><Badge variant={variant}>{label}</Badge></TableCell>
                    <TableCell>{formatDate(deck.generatedOn)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(deck)}
                          disabled={downloadingId === deck.id}
                          title="Download Word"
                        >
                          {downloadingId === deck.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Download className="w-3.5 h-3.5" />
                          }
                        </Button>
                        {canDistribute ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmDeck(deck)}
                            disabled={distribute.isPending}
                          >
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            Mark distributed
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground px-2">Distributed</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!confirmDeck} onOpenChange={open => { if (!open) setConfirmDeck(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Distribute this deck?</DialogTitle>
            <DialogDescription>
              This marks <strong>{confirmDeck?.coverTitle || 'this deck'}</strong> as Distributed and locks
              its {confirmDeck?.reportCount ?? 0} published report{confirmDeck?.reportCount === 1 ? '' : 's'} to
              read-only. Reports already locked or not yet published are skipped. This cannot be undone from here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeck(null)} disabled={distribute.isPending}>
              Cancel
            </Button>
            <Button onClick={() => handleDistribute()} disabled={distribute.isPending}>
              {distribute.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Distribute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
